import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Container from './Container';
import { getFeaturedCategories } from '@/data/categories';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';
import { pathFor } from '@/data/routes';
import { openCookiePreferences } from '@/components/legal/CookieBanner';
import SpanishPhraseOfDay from '@/components/widgets/SpanishPhraseOfDay';
import SocialProofBanner from '@/components/widgets/SocialProofBanner';
import InstallAppButton from '@/components/widgets/InstallAppButton';

/**
 * Footer — refonte Sprint 2 (2026-05-26, audit I·03).
 *
 * AVANT : 4 colonnes × jusqu'à 13 liens (44 entrées au total). Plus de
 *   liens qu'en nav principale, illisible, "footer-sitemap" SaaS.
 *
 * APRÈS : 4 colonnes × 5 liens MAX. Brand + Catégories (top 5) +
 *   Utile (top 5) + Légal (5). Le surplus (remote-work, gallery, voyage,
 *   enfants, consulting, partenaires, map, bookmarks, authors, presse,
 *   refund, etc.) migre vers /sitemap. Mention discrète "Plan du site →"
 *   en bas pour qui veut tout voir.
 *
 * Intégrations Sprint 1 conservées :
 *   - SocialProofBanner compact (4 stats sur une ligne)
 *   - SpanishPhraseOfDay compact
 *   - FlagBand variant="footer" (signature absolue couleurs drapeau)
 *   - 2 emails de contact (général + presse) — les 2 autres migrent dans /contact
 */
export default function Footer() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
  // Limite à 5 catégories featured (la fonction retourne déjà les pilier).
  const featuredCategories = getFeaturedCategories().slice(0, 5);

  const linkCls = "text-sm text-cream-50/85 hover:text-bordeaux-400 transition-colors";

  return (
    <footer className="relative overflow-hidden bg-navy-900 text-cream-50/90">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 flex items-center"
        style={{ width: 'min(60vw, 700px)' }}
      >
        <img
          src="/logo-symbol-creme.png"
          alt=""
          className="h-[180%] w-auto -translate-y-4 translate-x-1/4 opacity-[0.05]"
        />
      </div>

      <div className="relative">
        <Container className="py-16 md:py-20">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
            {/* === BRAND === */}
            <div className="lg:pr-6">
              <Link
                to={pathFor('home', lang)}
                aria-label={t('common:siteName')}
                className="inline-flex items-center gap-2.5"
              >
                {/* Logo V5 : symbole « repère + drapeau » + wordmark DM Serif
                    Display romain — papier + « Panama » sable sur footer sombre. */}
                <img
                  src="/logo-symbol.png"
                  alt=""
                  width="48"
                  height="48"
                  className="h-12 w-12 shrink-0 rounded-xl shadow-sm ring-1 ring-black/5"
                />
                <span className="font-serif text-2xl tracking-tight text-cream-50">
                  Inside <span className="text-cream-200">Panama</span>
                </span>
              </Link>
              <p className="mt-4 font-serif italic text-cream-50">{t('common:tagline')}</p>
              <p className="mt-3 text-sm leading-relaxed text-cream-50/70">{t('common:brief')}</p>

              {/* Contacts essentiels uniquement (2 sur 4). Le reste vit dans /contact. */}
              <ul className="mt-5 space-y-1.5 text-xs text-cream-50/75">
                <li>
                  <span className="text-cream-50/50">{t('components:footer.contactGeneral')} :</span>{' '}
                  <a href="mailto:hello@insidepanama.com" className="hover:text-bordeaux-400">hello@insidepanama.com</a>
                </li>
                <li>
                  <span className="text-cream-50/50">{t('components:footer.contactPress')} :</span>{' '}
                  <a href="mailto:presse@insidepanama.com" className="hover:text-bordeaux-400">presse@insidepanama.com</a>
                </li>
              </ul>

              {/* Installer l'app (PWA) — « mettre le site sur le bureau » : ouvre
                  le site en fenêtre standalone avec son icône (Chrome/Edge desktop
                  + Android ; hint sur iOS). Masqué si déjà installé / navigateur
                  sans install. Inspiré du « download the app » de The Real World. */}
              <InstallAppButton className="mt-6" />
            </div>

            {/* === CATÉGORIES (top 5) === */}
            <div>
              {/* V4 — label-mono pour les titres de colonnes footer
                  (Catégories / Utile / Légal). JetBrains Mono editorial,
                  plus dense que l'eyebrow legacy — alignement avec le rythme
                  V4 (Header wordmark Lora italic + colonnes mono). */}
              <h3 className="v4-label-mono text-gold-400 mb-4">{t('components:footer.categories')}</h3>
              <ul className="space-y-2.5">
                {featuredCategories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={pathFor('category', lang, { slug: c.slug })}
                      className={linkCls}
                    >
                      {c.label[lang] ?? c.label[DEFAULT_LANG]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* === UTILE (5 max — audit I·03) ===
                Avant : 13 entrées. Maintenant : Newsletter, Outils, Boutique,
                Carte, À propos. Le reste (remote-work, gallery, voyage,
                enfants, consulting, partenaires, bookmarks, presse) →
                /sitemap. */}
            <div>
              <h3 className="v4-label-mono text-gold-400 mb-4">{t('components:footer.useful')}</h3>
              <ul className="space-y-2.5">
                <li><Link to={pathFor('newsletter', lang)} className={linkCls}>{t('navigation:newsletter')}</Link></li>
                {/* Lead magnet « Move in 60 Days » — cours email gratuit (G1). */}
                <li><Link to={pathFor('move60', lang)} className={linkCls}>{t('components:footer.linkMove60', { defaultValue: lang === 'en' ? 'Move in 60 days (free course)' : lang === 'es' ? 'Múdate en 60 días (curso gratis)' : 'S\'installer en 60 jours (cours gratuit)' })}</Link></li>
                <li><Link to={pathFor('tools', lang)} className={linkCls}>{t('navigation:tools')}</Link></li>
                {/* Q&R : les ~390 reponses du corpus, reunies. Prerendu par
                    Astro pour les crawlers, interactif ici. Sans ce lien, la
                    page etait orpheline — donc invisible au maillage interne. */}
                <li><Link to={pathFor('questions', lang)} className={linkCls}>{t('components:footer.linkQuestions', { defaultValue: lang === 'en' ? 'Questions & answers' : lang === 'es' ? 'Preguntas y respuestas' : 'Questions & reponses' })}</Link></li>
                <li><Link to={pathFor('myRoadmap', lang)} className={linkCls}>{t('components:footer.linkMyRoadmap', { defaultValue: lang === 'en' ? 'My roadmap' : lang === 'es' ? 'Mi hoja de ruta' : 'Ma feuille de route' })}</Link></li>
                <li><Link to={pathFor('shop', lang)} className={linkCls}>{t('navigation:shop')}</Link></li>
                {/* Boutique Gumroad (page profil custom) — second point de vente,
                    catalogue complet trilingue hébergé chez Gumroad. */}
                <li><a href="https://insidepanama.gumroad.com" target="_blank" rel="noopener" className={linkCls}>{t('navigation:gumroadShop')} ↗</a></li>
                {/* /bibliotheque = page statique (mémos + consulting) hors React Router
                    → <a href> (un <Link> tomberait sur la 404 SPA). */}
                <li><a href="/bibliotheque" className={linkCls}>{t('components:footer.linkLibrary', { defaultValue: lang === 'en' ? 'Library & consulting' : lang === 'es' ? 'Biblioteca y consultoría' : 'Bibliothèque & consulting' })}</a></li>
                <li><Link to={pathFor('downloads', lang)} className={linkCls}>{t('components:footer.linkDownloads', { defaultValue: lang === 'en' ? 'Free wallpapers' : lang === 'es' ? 'Fondos gratis' : 'Fonds d\'écran gratuits' })}</Link></li>
                <li><Link to={pathFor('map', lang)} className={linkCls}>{t('components:footer.linkMap')}</Link></li>
                <li><Link to={pathFor('methodology', lang)} className={linkCls}>{t('components:footer.linkMethodology')}</Link></li>
              </ul>
            </div>

            {/* === LÉGAL (5 max — audit I·03) ===
                Avant : 9 entrées. Maintenant : Privacy, Terms, Cookies prefs,
                Disclosure, Contact. Le reste (notice, refund) → /sitemap. */}
            <div>
              <h3 className="v4-label-mono text-gold-400 mb-4">{t('components:footer.legal')}</h3>
              <ul className="space-y-2.5">
                <li><Link to={pathFor('legalPrivacy', lang)} className={linkCls}>{t('components:footer.privacy')}</Link></li>
                <li><Link to={pathFor('legalTerms', lang)} className={linkCls}>{t('components:footer.terms')}</Link></li>
                <li><Link to={pathFor('legalDisclosure', lang)} className={linkCls}>{t('components:footer.disclosure')}</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={openCookiePreferences}
                    className={linkCls}
                  >
                    {t('components:footer.linkCookiePrefs')}
                  </button>
                </li>
                <li><Link to={pathFor('legalContact', lang)} className={linkCls}>{t('components:footer.contact')}</Link></li>
              </ul>
            </div>
          </div>

          {/* "Plan du site →" discret — sortie pour utilisateurs qui cherchent
              une page hors top-5 (presse, remote-work, gallery, partenaires…). */}
          <div className="mt-10 flex flex-col gap-3 border-t border-cream-50/10 pt-6 text-xs text-cream-50/60 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-mono uppercase tracking-[0.18em]">{t('components:footer.allPagesLabel', { defaultValue: lang === 'en' ? 'Advanced pages' : lang === 'es' ? 'Páginas avanzadas' : 'Pages avancées' })}</p>
            <Link
              to={pathFor('sitemap', lang)}
              className="inline-flex items-center gap-1.5 font-medium text-cream-50/80 hover:text-bordeaux-400"
            >
              {t('components:footer.allPagesLink', { defaultValue: lang === 'en' ? 'Full site map →' : lang === 'es' ? 'Mapa del sitio completo →' : 'Plan du site complet →' })}
            </Link>
          </div>
        </Container>

        {/* Bande sociale proof compacte (4 stats) — déplacée ici depuis la home
            Sprint 1 (audit C·01). Sans animation, valeurs figées. */}
        <div className="border-t border-cream-50/10 bg-navy-900/40">
          <Container className="py-7">
            <SocialProofBanner compact />
          </Container>
        </div>

        <div className="border-t border-cream-50/10">
          <Container className="py-6">
            <SpanishPhraseOfDay compact />
          </Container>
        </div>

        <div className="border-t border-cream-50/10">
          <Container className="flex flex-col gap-3 py-5 text-xs text-cream-50/80 md:flex-row md:items-center md:justify-between">
            <p>© {year} Inside Panama. {t('components:footer.rights')}</p>
            <p className="md:max-w-xl md:text-center">{t('components:footer.affiliateDisclaimer')}</p>
            <p className="font-medium tracking-wide">{t('components:footer.madeIn')}</p>
          </Container>
        </div>

        {/* Identité légale SA — boost trust + transparence (audit backlog 26 mai).
            Affichée discrètement en pied absolu : raison sociale + RUC enregistré
            à Panama. Aligne le site avec le pack juridique V2 + LS billing info. */}
        <div className="border-t border-cream-50/10 bg-navy-900/60">
          <Container className="py-3 text-center text-[11px] leading-relaxed text-cream-50/60">
            <span className="font-mono">
              Édité par <strong className="text-cream-50/80">LAS FUNDADORAS HERMANAS S.A.</strong>
              {' · '}
              RUC <span className="tracking-wider">155776412-2-2025</span>
              {' · '}
              Aviso de Operación 155776412-2-2025-2026-574435444
              {' · '}
              Volcán, Chiriquí, Panamá
            </span>
          </Container>
        </div>
      </div>
    </footer>
  );
}
